// The spoken shortcut cue: near the end of a recording the clinician says
// "insert <shortcut name>" and that saved shortcut goes into the note.
//
// Rules (the same as the iOS recorder, agreed with the clinical lead):
// - Only the end of the recording counts (the last CUE_WINDOW_SECONDS).
//   Stored transcripts carry no timings, so the window is the same share of
//   the words as of the recording's duration.
// - Only the clinician's saved shortcut names match. Case, punctuation and
//   spacing are ignored and small transcription slips are tolerated with the
//   thresholds of spoken-macros.ts; short names and words of up to three
//   letters must match exactly.
// - "insert" must open its clause (start of a sentence, after a comma, or
//   after a lead-in like "okay" / "please") and the name must close it
//   ("…, please" is fine). So "normal cardiac exam" said during the visit,
//   "the nurse will insert normal saline" or "don't insert normal knee exam"
//   never fire.
// - "…, scratch that" / "cancel that" right after takes it back.

// The same text helpers as apps/api/src/lib/spoken-macros.ts, kept here so
// the browser can detect cues live with exactly the server's rules.
export const levenshtein = (a: string, b: string) => {
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++)
      row[j] = Math.min(
        (previous[j] as number) + 1,
        (row[j - 1] as number) + 1,
        (previous[j - 1] as number) + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    previous = row;
  }
  return previous[b.length] as number;
};
// Lower case without accents, with one kind of apostrophe.
export const foldCueText = (text: string) =>
  text
    .toLowerCase()
    .replace(/\u0153/g, "oe")
    .replace(/\u00e6/g, "ae")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2018\u2019\u02bc\u0060\u00b4]/g, "'");
const fold = foldCueText;
const plain = (word: string) => fold(word).replace(/[^a-z0-9']/g, "");

export const CUE_WINDOW_SECONDS = 60;
// Used when the duration is unknown: about a minute of speech.
const WORDS_PER_WINDOW = 150;

export type CueShortcut = { trigger: string };
export type DetectedCue = {
  trigger: string;
  // The words as heard ("insert normal cardiac exam").
  spoken: string;
};

type Token = { plain: string; raw: string; breakAfter: boolean };

// "Speaker 1:" / "Speaker 1 [Clinician]:" labels and the line of asterisks
// between recordings end a clause and are not words.
const LABEL = /(^|\s)Speaker \d{1,2}(?: \[[^\]\n]{1,48}\])?:(?=\s|$)/g;
const SEPARATOR = /\*{10,}/g;
const BREAK_AFTER = /[.!?;:,—–]["'”’)]*$/;

const LEAD_INS = new Set([
  "okay",
  "ok",
  "alright",
  "right",
  "so",
  "and",
  "also",
  "now",
  "then",
  "please",
  "rosc",
  "next",
  "lastly",
  "finally",
  "oh",
  "um",
  "uh",
  "et",
  "alors",
  "bon",
  "ensuite",
  "puis",
  "aussi",
  "enfin",
]);
const FILLERS = new Set([
  "the",
  "a",
  "an",
  "my",
  "our",
  "le",
  "la",
  "les",
  "un",
  "une",
  "mon",
  "ma",
  "mes",
]);
const TAILS = new Set([
  "please",
  "thanks",
  "thank",
  "you",
  "now",
  "merci",
  "svp",
  "stp",
]);
const CANCELS = [
  ["scratch", "that"],
  ["cancel", "that"],
  ["cancel", "it"],
  ["never", "mind"],
  ["no", "wait"],
  ["wait", "no"],
  ["delete", "that"],
  ["strike", "that"],
  ["forget", "that"],
  ["forget", "it"],
  ["undo", "that"],
  ["efface", "ca"],
  ["annule", "ca"],
  ["oublie", "ca"],
];

export const isInsertVerb = (word: string) =>
  ["insert", "insere", "inserer", "inserez"].includes(word) ||
  // ASR slips ("insurt", "inserts"), not other verbs ("invert").
  (word.startsWith("ins") &&
    word.length >= 5 &&
    levenshtein(word, "insert") <= 1);

// The clause right after the shortcut takes it back. A bare "cancel" counts
// only as the whole clause ("cancel the referral" is something else).
const isCancel = (clause: Token[]) => {
  const words = clause.map((token) => token.plain);
  while (["oh", "sorry", "actually", "um", "uh"].includes(words[0] ?? ""))
    words.shift();
  if (
    words.length === 1 &&
    ["cancel", "annule", "nevermind"].includes(words[0] ?? "")
  )
    return true;
  return CANCELS.some((cancel) =>
    cancel.every((word, index) => words[index] === word),
  );
};

const tokenize = (transcript: string): Token[] => {
  const text = transcript.replace(LABEL, "$1\n").replace(SEPARATOR, "\n");
  const tokens: Token[] = [];
  for (const piece of text.split(/(\s+)/)) {
    if (!piece) continue;
    if (/^\s+$/.test(piece)) {
      const last = tokens[tokens.length - 1];
      if (piece.includes("\n") && last) last.breakAfter = true;
      continue;
    }
    const word = plain(piece);
    const ends = BREAK_AFTER.test(piece);
    if (!word) {
      const last = tokens[tokens.length - 1];
      if (ends && last) last.breakAfter = true;
      continue;
    }
    tokens.push({
      plain: word,
      raw: piece.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ""),
      breakAfter: ends,
    });
  }
  return tokens;
};

const nameWords = (name: string) =>
  name.trim().split(/\s+/).map(plain).filter(Boolean);

export const detectEndOfVisitCues = (
  transcript: string,
  shortcuts: readonly CueShortcut[],
  { durationSeconds }: { durationSeconds?: number | null } = {},
): DetectedCue[] => {
  const triggers = shortcuts.flatMap((shortcut) => {
    const words = nameWords(shortcut.trigger);
    return words.length ? [{ shortcut, words }] : [];
  });
  if (!triggers.length || !transcript.trim()) return [];
  const tokens = tokenize(transcript);
  if (!tokens.length) return [];
  const tail =
    durationSeconds && durationSeconds > 0
      ? Math.ceil(
          tokens.length * Math.min(1, CUE_WINDOW_SECONDS / durationSeconds),
        )
      : WORDS_PER_WINDOW;
  const windowStart = Math.max(0, tokens.length - tail);

  const clauses: { tokens: Token[]; start: number }[] = [];
  let current: Token[] = [];
  let start = 0;
  tokens.forEach((token, index) => {
    if (!current.length) start = index;
    current.push(token);
    if (token.breakAfter) {
      clauses.push({ tokens: current, start });
      current = [];
    }
  });
  if (current.length) clauses.push({ tokens: current, start });

  // One cue per shortcut: the last time it was said.
  const found = new Map<string, DetectedCue & { at: number }>();
  clauses.forEach((clause, clauseIndex) => {
    const verb = clause.tokens.findIndex((token) => isInsertVerb(token.plain));
    if (verb < 0) return;
    if (
      !clause.tokens.slice(0, verb).every((token) => LEAD_INS.has(token.plain))
    )
      return;
    if (clause.start + verb < windowStart) return;
    // "Insert, normal knee exam.": the name is in the next clause.
    const joined = verb === clause.tokens.length - 1;
    const words = joined
      ? [...clause.tokens, ...(clauses[clauseIndex + 1]?.tokens ?? [])]
      : clause.tokens;
    let first = verb + 1;
    while (first < words.length && FILLERS.has(words[first]?.plain ?? ""))
      first++;
    const spoken = words.slice(first).map((token) => token.plain);
    // French elision: "insère l'examen du genou".
    const elided = spoken[0]?.match(/^[ld]'(.+)$/);
    if (elided?.[1]) spoken[0] = elided[1];
    if (!spoken.length) return;

    let best: { trigger: string; length: number; distance: number } | null =
      null;
    for (const { shortcut, words: name } of triggers) {
      if (name.length > spoken.length) continue;
      // The name must end the clause (a polite tail is fine).
      if (!spoken.slice(name.length).every((word) => TAILS.has(word))) continue;
      const span = spoken.slice(0, name.length);
      if (!name.every((word, index) => word.length > 3 || word === span[index]))
        continue;
      const phrase = name.join(" ");
      const short =
        phrase.length <= 3 || (name.length === 1 && phrase.length <= 6);
      const tolerance = short
        ? 0
        : phrase.length < 12
          ? 1
          : Math.max(2, Math.floor(phrase.length * 0.12));
      const distance = levenshtein(span.join(" "), phrase);
      if (distance > tolerance) continue;
      if (
        !best ||
        name.length > best.length ||
        (name.length === best.length && distance < best.distance)
      )
        best = { trigger: shortcut.trigger, length: name.length, distance };
    }
    if (!best) return;
    const next = clauses[clauseIndex + (joined ? 2 : 1)];
    if (next && isCancel(next.tokens)) return;
    const key = fold(best.trigger.trim());
    found.delete(key);
    found.set(key, {
      trigger: best.trigger,
      spoken: words
        .slice(verb, first + best.length)
        .map((token) => token.raw)
        .join(" "),
      at: clause.start + verb,
    });
  });
  return [...found.values()]
    .sort((a, b) => a.at - b.at)
    .map(({ trigger, spoken }) => ({ trigger, spoken }));
};
